import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import proptypes from 'prop-types';
const { func, oneOf, shape, string } = proptypes;

const LensCornerBorder = ({ xEdge, yEdge, boundingBox, imageSize }) => {
  const width = imageSize.width * (boundingBox.xMax - boundingBox.xMin);
  const height = imageSize.height * (boundingBox.yMax - boundingBox.yMin);
  let size = Math.min(width, height) * 0.25;
  if (size < 14) {
    size = Math.min(width, height) * 0.35; // Size for very small objects
  }

  let borderClasses = '';
  if (xEdge === 'left' && yEdge === 'top') {
    borderClasses = 'sui-border-t-4 sui-rounded-tl-[10px] sui-border-l-4 sui-border-b-0 sui-border-r-0';
  } else if (xEdge === 'right' && yEdge === 'top') {
    borderClasses = 'sui-border-t-4 sui-rounded-tr-[10px] sui-border-r-4 sui-border-b-0 sui-border-l-0';
  } else if (xEdge === 'right' && yEdge === 'bottom') {
    borderClasses = 'sui-border-b-4 sui-rounded-br-[10px] sui-border-r-4 sui-border-t-0 sui-border-l-0';
  } else if (xEdge === 'left' && yEdge === 'bottom') {
    borderClasses = 'sui-border-b-4 sui-rounded-bl-[10px] sui-border-l-4 sui-border-t-0 sui-border-r-0';
  }

  return (
    <div
      className={`sui-absolute sui-${yEdge}-0 sui-${xEdge}-0`}
      style={{ width: `${size}px`, height: `${size}px` }}
    >
      <div
        className={classNames(
          'sui-absolute sui-border-solid sui-border-[white] sui-w-full sui-h-full',
          borderClasses
        )}
      />
    </div>
  );
};

LensCornerBorder.propTypes = {
  xEdge: oneOf(['left', 'right']).isRequired,
  yEdge: oneOf(['top', 'bottom']).isRequired,
};

// TODO: The current implementation uses CSS styling to build the UI and behavior. In a future sprint, we might consider
// experimenting with a different approach to build this using a <canvas> element. One potential issue with the current
// implementation is the background image may not get sized correctly when the user adjusts the zoom level or viewport.
const HotspotLensOverlay = ({
  boundingBox,
  imageRef,
  imageUrl,
}) => {
  const [imageSize, setImageSize] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const { xMin, xMax, yMin, yMax } = boundingBox;
  const { height, width } = imageSize;
  const boundingBoxValues = {
    xMin: Number(xMin),
    xMax: Number(xMax),
    yMin: Number(yMin),
    yMax: Number(yMax),
  };

  const updateImageSize = (_event, ref) => {
    /* istanbul ignore else */
    if (ref?.current) {
      const imageBoundingClientRect = ref.current.getBoundingClientRect();
      setImageSize({ width: imageBoundingClientRect.width, height: imageBoundingClientRect.height });
    }
  };

  useEffect(() => {
    updateImageSize(null, imageRef);
  }, [imageRef]);

  /* istanbul ignore next */
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', (event) => updateImageSize(event, imageRef));
    }
    return () => window.removeEventListener('resize', (event) => updateImageSize(event, imageRef));
  }, [imageRef]);

  useEffect(() => {
    setIsLoading(!height);
  }, [height]);

  useEffect(() => {
    setIsLoading(!width);
  }, [width]);

  return (
    <div
      aria-label="Lens Overlay"
      className={classNames('sui-absolute', { 'sui-invisible': isLoading })}
      style={{
        top: `${yMin * 100}%`,
        right: `${(1 - xMax) * 100}%`,
        bottom: `${(1 - yMax) * 100}%`,
        left: `${xMin * 100}%`,
      }}
    >
      <div
        aria-label="Lens Background Image"
        // eslint-disable-next-line tailwindcss/no-arbitrary-value
        className="sui-h-[calc(100%-4px)] sui-m-2px sui-rounded-lg sui-overflow-hidden"
        style={{
          background: `url(${imageUrl})`,
          backgroundSize: `${width}px ${height}px`,
          backgroundPosition: `-${width * Number(xMin) + 2}px -${height * Number(yMin) + 2}px`,
        }}
      />
      <LensCornerBorder
        yEdge="top"
        xEdge="left"
        boundingBox={boundingBoxValues}
        imageSize={{ width, height }}
      />
      <LensCornerBorder
        yEdge="top"
        xEdge="right"
        boundingBox={boundingBoxValues}
        imageSize={{ width, height }}
      />
      <LensCornerBorder
        yEdge="bottom"
        xEdge="right"
        boundingBox={boundingBoxValues}
        imageSize={{ width, height }}
      />
      <LensCornerBorder
        yEdge="bottom"
        xEdge="left"
        boundingBox={boundingBoxValues}
        imageSize={{ width, height }}
      />
    </div>
  );
};

HotspotLensOverlay.displayName = 'HotspotLensOverlay';

HotspotLensOverlay.propTypes = {
  boundingBox: shape({
    xMin: string,
    xMax: string,
    yMin: string,
    yMax: string,
  }).isRequired,
  imageRef: shape({ current: shape({ getBoundingClientRect: func }) }),
  imageUrl: string,
};

HotspotLensOverlay.defaultProps = {
  imageRef: null,
  imageUrl: '',
};

export { HotspotLensOverlay };
